import {
  deepEqual,
  SpinnerV2Service,
  StorageV2Service,
  StoreV2,
  GridUtilsV2,
  GridState,
  GridSortParam,
  GridStateChange,
  RadioRowOption,
  MultiselectV2SimpleItemSource,
  unique,
  getArrayDiff,
  MultiselectV2ObservableItemSource,
} from '@schenck/rotec-components';
import { of, Subject, BehaviorSubject, Observable } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  skip,
  switchMap,
  startWith,
  mapTo,
} from 'rxjs/operators';
import { ResponsibleCompany, TaskBase } from '../api';
import { SettingsServiceBase } from './settings.service';

type StringKeys<T> = Extract<keyof T, string>;

export type FilterMapping<T> = {
  [K in keyof T]?: (k: T[K], filterValue: any) => boolean;
};

export interface SearchState<Result extends TaskBase>
  extends GridState<Result, StringKeys<Result>> {
  /**
   * Selected result ids.
   */
  selectedIds: string[];
}

export interface SearchStateChange<Result extends TaskBase>
  extends GridStateChange<StringKeys<Result>> {
  pageSize?: number;
}

export interface SearchSettings<Result extends TaskBase> {
  /**
   * Used for Spinner Service and Storage.
   */
  key: string;
  /**
   * Which of the fields is the primary date field. Used to determine if
   * a filter change can be handled in frontend only or if the
   * backend has to be asked for data (this is the case for the primary date prop).
   */
  primaryDateProp: StringKeys<Result>;
  /**
   * For each possible filter a mapping what this filter does.
   */
  filter: FilterMapping<Result>;
  /**
   * Comparator used for sorting the results.
   * By default, dates/strings/numbers will be handled,
   * so if this is enough for you, you don't need to set this.
   */
  searchResultComparator?: (resultA: Result, resultB: Result, sortBy: GridSortParam) => number;
}

export interface SearchApi<Result> {
  /**
   * Searches for results with given date.
   */
  search$(date: { from: Date; to: Date } | null): Observable<Result[]>;
  /**
   * Should emit whenever the search should be reloaded.
   */
  reload$: Observable<any>;
}

/**
 * Convenience base class to use to create a generic search service.
 * This service only searches for results in the backend on date range filter changes,
 * everything else will be done in the frontend directly (sorting/filtering/pagination).
 */
export abstract class SearchServiceBase<Result extends TaskBase> extends StoreV2<
  SearchState<Result>
> {
  private _search$ = new Subject<SearchStateChange<Result>>();
  private _searchError$ = new BehaviorSubject<string | boolean>(false);

  private settings: SearchSettings<Result>;
  private _allResults$ = new BehaviorSubject<Result[]>([]);
  /**
   * All results that are returned from the backend, unfiltered and not paginated.
   * Can be used for example as input for filter classes.
   */
  allResults$ = this._allResults$.asObservable();

  responsibleCompanyOptions: RadioRowOption<ResponsibleCompany | 'All'>[] = [
    { value: 'All', name: 'All' },
    { value: ResponsibleCompany.Customer, name: 'My' },
    { value: ResponsibleCompany.Schenck, name: 'Schenck' },
  ];
  responsibleCompanyFilterValue$: Observable<ResponsibleCompany | 'All'> = this.state$.pipe(
    map((state) => {
      const filterValue =
        state.filter && state.filter.find((filter) => filter.key === 'responsibleCompany');
      if (filterValue) {
        return filterValue.value;
      }
      return 'All';
    })
  );

  constructor(
    private searchApi: SearchApi<Result>,
    private loadingService: SpinnerV2Service,
    private settingsService: SettingsServiceBase<any, any, any>,
    storageService: StorageV2Service,
    settings: SearchSettings<Result>
  ) {
    super(
      {
        page: 0,
        pageSize: 15,
        fulltext: '',
        filter: [],
        sort: [],
        results: [],
        total: 0,
        loading: true,
        selectedIds: [],
      },
      settings.key,
      {
        name: settings.key,
        storageService,
        deserializer: deserialzeStateAndParseDates,
        serializer: serializeStateIgnoreResults,
      }
    );

    this.initSettings(settings);
    // setup listeners
    this.setupBackendSearchListener();
    this.setupSettingsListener();
    // Do new search with potentially saved parameters
    this.state.results = [];
    this.state.total = 0;
    this._search$.next({
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
    });
  }

  public get searchError$(): Observable<string | boolean> {
    return this._searchError$.asObservable();
  }

  /**
   * Invokes a search.
   * All parameters that are not present will be reused.
   */
  search(params: Partial<SearchStateChange<Result>>) {
    const currSearchParams: SearchStateChange<Result> = {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
    };
    const newSearchParams: SearchStateChange<Result> = {
      ...currSearchParams,
      ...params,
    };

    this.patchState('update search params', newSearchParams);

    if (this.hasPrimaryDateFilterChanged(newSearchParams, currSearchParams)) {
      this._search$.next(newSearchParams);
    } else {
      this.patchState('update results', this.getResults());
    }
  }

  /**
   * Fast filter for responsible company.
   */
  filterForResponsible(value: ResponsibleCompany | 'All') {
    const filter = (this.state.filter || []).filter((f) => f.key !== 'responsibleCompany');
    if (value !== 'All') {
      filter.push({ key: <any>'responsibleCompany', value });
    }
    this.search({ filter });
  }

  /**
   * Returns a multiselect item source for given entity key, either with translation or not.
   */
  getMultiselectItemSource(entity: keyof Result, translateFn?: (value: any) => string) {
    if (!translateFn) {
      return new MultiselectV2ObservableItemSource(this.allResults$, entity);
    } else {
      return new MultiselectV2SimpleItemSource(
        this.allResults$.pipe(
          map((values) => values.map((value) => value[entity]).filter((vl) => !!vl)),
          map((values) => unique(values)),
          map((values) => values.map((value: any) => ({ name: translateFn(value), value })))
        )
      );
    }
  }

  /**
   * Returns all results that are selected.
   */
  getSelectedResults() {
    return this._allResults$.value
      .filter((res) => this.state.selectedIds.includes(res.id))
      .filter((res) => !!res);
  }

  /**
   * Deselects all results.
   * @deprecated to be removed after redesign
   */
  clearAllSelected() {
    this.patchState('deselect all', { selectedIds: [] });
  }

  /**
   * Set the ids that are selected.
   */
  setSelectedResults(selectedIds: string[]) {
    this.patchState('set selected results', { selectedIds });
  }

  getSettingsKey(): string {
    return this.settings.key;
  }

  /**
   * Determines whether or not the primary date filter has changed.
   */
  private hasPrimaryDateFilterChanged(
    newSearchParams: GridStateChange<StringKeys<Result>>,
    currSearchParams: GridStateChange<StringKeys<Result>>
  ) {
    const filterDiff = getArrayDiff(newSearchParams.filter, currSearchParams.filter, deepEqual);
    const dateFilterDiff = filterDiff.filter(
      (filter) => this.settings.primaryDateProp === filter.key
    );
    return dateFilterDiff.length > 0;
  }

  /**
   * Return the list of results on a certain page after filtering and sorting.
   */
  private getResults() {
    return GridUtilsV2.getResults(this._allResults$.value, {
      filter: this.state.filter,
      fulltext: this.state.fulltext,
      page: this.state.page,
      pageSize: this.state.pageSize,
      sort: this.state.sort,
      filterFnMap: this.settings.filter,
      resultComparator: this.settings.searchResultComparator,
    });
  }

  /**
   * React to settings page size change by doing new search with new page size.
   */
  private setupSettingsListener() {
    this.settingsService.settings$
      .pipe(
        map((settings) => settings.pageSize),
        distinctUntilChanged(),
        skip(1)
      )
      .subscribe((pageSize) => {
        this.search({ pageSize });
      });
  }

  /**
   * Setup listener for "real" backend search.
   * Everytime the observable is invoked or the list should be reloaded, a backend call will be made.
   */
  private setupBackendSearchListener() {
    this._search$
      .pipe(
        switchMap((params) => this.searchApi.reload$.pipe(startWith(<void>null), mapTo(params))),
        switchMap((params) => {
          this.setLoading(true);
          this.patchState('searching in backend..', { loading: true });
          const primaryDateFilter = (params.filter || []).find(
            (filter) => filter.key === this.settings.primaryDateProp
          );
          return this.searchApi.search$(primaryDateFilter && primaryDateFilter.value).pipe(
            catchError((error) => {
              this._searchError$.next(error.error?.message || error?.message || true);
              return of(<Result[]>[]);
            })
          );
        })
      )
      .subscribe((results) => {
        this.setLoading(false);
        this._allResults$.next(results);
        this.patchState('got results from backend', { ...this.getResults(), loading: false });
      });
  }

  /**
   * Set loading state of search.
   */
  private setLoading(loading: boolean) {
    this.loadingService.setLoading(this.settings.key, loading);
  }

  private initSettings(settings: SearchSettings<Result>) {
    this.settings = { ...settings };
    // If no search comparator is provided, add the default one.
    if (!this.settings.searchResultComparator) {
      this.settings.searchResultComparator = GridUtilsV2.defaultResultComparator;
    }
  }
}

function serializeStateIgnoreResults<Result extends TaskBase>(state: SearchState<Result>): string {
  if (!state) {
    return null;
  }
  const stateWithoutResults: SearchState<Result> = {
    ...state,
    results: [],
    selectedIds: [],
  };
  return JSON.stringify(stateWithoutResults);
}

function deserialzeStateAndParseDates<Result extends TaskBase>(storage: string) {
  const state: SearchState<Result> = JSON.parse(storage);
  if (!state) {
    return null;
  }

  // ignore results (should not be set anyway because of deserializer)
  state.results = [];
  // ignore selected (should not be set anyway because of deserializer)
  state.selectedIds = [];
  // parse date props in filter
  state.filter.forEach((filter) => {
    if (filter.value.from || filter.value.to) {
      filter.value = {
        from: filter.value.from && new Date(filter.value.from),
        to: filter.value.to && new Date(filter.value.to),
      };
    }
  });
  return state;
}
